/**
 * EditModal - Reusable modal component for editing product configurations
 * Used by M07 (Result) and M13 (Share) screens to edit:
 * - XUM main plan (9 fields)
 * - R1/R2/R3/R4/R5 riders (sub-options)
 *
 * Pattern matches Curex 9_input screenshots:
 *   - Header with title + close button
 *   - Numbered fields with helper text
 *   - Footer: Hủy (cancel) + Lưu chỉnh sửa (save)
 */

import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  TextInput,
  Platform,
} from 'react-native';
import { Icon } from './Icon';
import { colors, spacing, fontWeights, borderRadius } from '../theme';

export type EditFieldType = 'text' | 'number' | 'dropdown' | 'stepper' | 'checkbox';

export interface EditFieldOption {
  label: string;
  value: string | number;
}

export interface EditField {
  /** Field number (1-based, displayed as "01", "02", ...) */
  index: number;
  /** Label displayed left of input */
  label: string;
  /** Optional secondary text below label (e.g. "Tối đa: 25 triệu") */
  hint?: string;
  /** Input type */
  type: EditFieldType;
  /** Current value (string for all types; number values should be stringified) */
  value: string;
  /** On change callback — receives the new string value */
  onChange: (v: string) => void;
  /** For type=dropdown: options */
  options?: EditFieldOption[];
  /** For type=stepper: min, max, step */
  min?: number;
  max?: number;
  step?: number;
  /** Optional prefix/suffix for display (e.g. "đ") */
  prefix?: string;
  /** Validation: warning text shown below input if invalid */
  warning?: string | null;
}

export interface EditModalProps {
  visible: boolean;
  title: string;
  subtitle?: string;
  fields: EditField[];
  onCancel: () => void;
  onSave: () => void;
  saveLabel?: string;
}

export const EditModal: React.FC<EditModalProps> = ({
  visible,
  title,
  subtitle,
  fields,
  onCancel,
  onSave,
  saveLabel = 'Lưu chỉnh sửa',
}) => {
  const [openDropdownIdx, setOpenDropdownIdx] = useState<number | null>(null);
  const [focusedFieldIdx, setFocusedFieldIdx] = useState<number | null>(null);

  if (!visible) return null;

  const renderField = (field: EditField) => {
    const numberLabel = `${String(field.index).padStart(2, '0')}`;

    switch (field.type) {
      case 'dropdown': {
        const isOpen = openDropdownIdx === field.index;
        const selectedOption = field.options?.find((o) => String(o.value) === String(field.value));
        return (
          <View key={field.index} style={styles.fieldContainer}>
            <Text style={styles.fieldLabel}>
              <Text style={styles.fieldIndex}>{numberLabel}</Text> {field.label}
              {field.hint ? ` *` : ''}
            </Text>
            <TouchableOpacity
              style={[styles.dropdown, isOpen && styles.dropdownOpen]}
              onPress={() => setOpenDropdownIdx(isOpen ? null : field.index)}
              accessibilityRole="button"
              accessibilityLabel={`Chọn ${field.label}`}
            >
              <Text style={styles.dropdownText}>{selectedOption?.label || field.value || '—'}</Text>
              <Icon
                name={isOpen ? 'chevron_left' : 'chevron_right'}
                size={18}
                color={colors.onSurfaceVariant}
              />
            </TouchableOpacity>
            {isOpen && field.options && field.options.length > 0 && (
              <View style={styles.dropdownMenu}>
                {field.options.map((opt) => {
                  const isSelected = String(opt.value) === String(field.value);
                  return (
                    <TouchableOpacity
                      key={String(opt.value)}
                      style={[styles.dropdownOption, isSelected && styles.dropdownOptionSelected]}
                      onPress={() => {
                        field.onChange(String(opt.value));
                        setOpenDropdownIdx(null);
                      }}
                      accessibilityRole="menuitem"
                    >
                      <Text style={[styles.dropdownOptionText, isSelected && styles.dropdownOptionTextSelected]}>
                        {opt.label}
                      </Text>
                      {isSelected && <Icon name="check" size={16} color={colors.primary} />}
                    </TouchableOpacity>
                  );
                })}
              </View>
            )}
            {field.hint && <Text style={styles.fieldHint}>{field.hint}</Text>}
            {field.warning && (
              <Text style={styles.fieldWarning}>⚠ {field.warning}</Text>
            )}
          </View>
        );
      }

      case 'stepper':
        return (
          <View key={field.index} style={styles.fieldContainer}>
            <Text style={styles.fieldLabel}>
              <Text style={styles.fieldIndex}>{numberLabel}</Text> {field.label}
            </Text>
            <View style={styles.stepperRow}>
              <TouchableOpacity
                style={styles.stepperBtn}
                onPress={() => {
                  const num = parseInt(field.value, 10) || 0;
                  const newVal = Math.max(field.min ?? 0, num - (field.step ?? 1));
                  field.onChange(String(newVal));
                }}
              >
                <Text style={styles.stepperBtnText}>-</Text>
              </TouchableOpacity>
              <View style={styles.stepperValueBox}>
                <Text style={styles.stepperValueText}>{field.value}</Text>
              </View>
              <TouchableOpacity
                style={styles.stepperBtn}
                onPress={() => {
                  const num = parseInt(field.value, 10) || 0;
                  const newVal = Math.min(field.max ?? 999, num + (field.step ?? 1));
                  field.onChange(String(newVal));
                }}
              >
                <Text style={styles.stepperBtnText}>+</Text>
              </TouchableOpacity>
            </View>
            {field.hint && <Text style={styles.fieldHint}>{field.hint}</Text>}
          </View>
        );

      case 'checkbox':
        return (
          <View key={field.index} style={styles.checkboxRow}>
            <TouchableOpacity
              style={[styles.checkbox, field.value === 'true' && styles.checkboxChecked]}
              onPress={() => field.onChange(field.value === 'true' ? 'false' : 'true')}
            >
              {field.value === 'true' && (
                <Icon name="check" size={14} color="#ffffff" />
              )}
            </TouchableOpacity>
            <Text style={styles.checkboxLabel}>{field.label}</Text>
          </View>
        );

      case 'number':
      default: {
        const isFocused = focusedFieldIdx === field.index;
        return (
          <View key={field.index} style={styles.fieldContainer}>
            <Text style={styles.fieldLabel}>
              <Text style={styles.fieldIndex}>{numberLabel}</Text> {field.label}
            </Text>
            <View style={[styles.inputBox, isFocused && styles.inputBoxFocused]}>
              <TextInput
                style={styles.input}
                value={field.value}
                onChangeText={field.onChange}
                keyboardType={field.type === 'number' ? 'numeric' : 'default'}
                placeholder={field.hint}
                onFocus={() => setFocusedFieldIdx(field.index)}
                onBlur={() => setFocusedFieldIdx(null)}
              />
              {field.prefix && <Text style={styles.inputSuffix}>{field.prefix}</Text>}
            </View>
            {field.hint && <Text style={styles.fieldHint}>{field.hint}</Text>}
            {field.warning && (
              <Text style={styles.fieldWarning}>⚠ {field.warning}</Text>
            )}
          </View>
        );
      }
    }
  };

  return (
    <View style={styles.modalBackdrop}>
      <TouchableOpacity
        style={StyleSheet.absoluteFill}
        activeOpacity={1}
        onPress={onCancel}
      />
      <View style={styles.modalCard}>
        {/* Header */}
        <View style={styles.modalHeader}>
          <View style={{ flex: 1 }}>
            <Text style={styles.modalTitle}>{title}</Text>
            {subtitle && <Text style={styles.modalSubtitle}>{subtitle}</Text>}
          </View>
          <TouchableOpacity onPress={onCancel} style={styles.closeBtn}>
            <Icon name="close" size={22} color={colors.onSurfaceVariant} />
          </TouchableOpacity>
        </View>

        {/* Fields */}
        <ScrollView style={styles.modalBody} showsVerticalScrollIndicator>
          {fields.map(renderField)}
        </ScrollView>

        {/* Footer */}
        <View style={styles.modalFooter}>
          <TouchableOpacity style={styles.cancelBtn} onPress={onCancel}>
            <Text style={styles.cancelBtnText}>Đóng</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.saveBtn} onPress={onSave}>
            <Text style={styles.saveBtnText}>{saveLabel}</Text>
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  modalBackdrop: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing[4],
    zIndex: 1000,
  },
  modalCard: {
    backgroundColor: '#ffffff',
    borderRadius: borderRadius.md,
    width: '100%',
    maxWidth: 540,
    maxHeight: '90%',
    overflow: 'hidden',
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: spacing[4],
    borderBottomWidth: 1,
    borderBottomColor: colors.outlineVariant,
    gap: spacing[2],
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: fontWeights.bold,
    color: colors.onSurface,
  },
  modalSubtitle: {
    fontSize: 12,
    color: colors.onSurfaceVariant,
    marginTop: 2,
  },
  closeBtn: {
    padding: spacing[1],
  },
  modalBody: {
    padding: spacing[4],
    maxHeight: 480,
  },
  modalFooter: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: spacing[2],
    padding: spacing[3],
    borderTopWidth: 1,
    borderTopColor: colors.outlineVariant,
  },
  cancelBtn: {
    paddingHorizontal: spacing[4],
    paddingVertical: spacing[2.5],
    borderRadius: borderRadius.md,
  },
  cancelBtnText: {
    fontSize: 14,
    fontWeight: fontWeights.semibold,
    color: colors.onSurfaceVariant,
  },
  saveBtn: {
    paddingHorizontal: spacing[5],
    paddingVertical: spacing[2.5],
    borderRadius: borderRadius.md,
    backgroundColor: colors.primary,
  },
  saveBtnText: {
    fontSize: 14,
    fontWeight: fontWeights.bold,
    color: '#ffffff',
  },
  // Field styles
  fieldContainer: {
    marginBottom: spacing[3],
  },
  fieldLabel: {
    fontSize: 13,
    fontWeight: fontWeights.semibold,
    color: colors.onSurface,
    marginBottom: spacing[1.5],
  },
  fieldIndex: {
    color: colors.onSurfaceVariant,
    fontWeight: fontWeights.regular,
  },
  fieldHint: {
    fontSize: 11,
    color: colors.onSurfaceVariant,
    marginTop: spacing[1],
  },
  fieldWarning: {
    fontSize: 11,
    color: '#dc2626',
    marginTop: spacing[1],
  },
  // Input
  inputBox: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: colors.outline,
    borderRadius: borderRadius.DEFAULT,
    paddingHorizontal: spacing[2.5],
    height: 38,
  },
  inputBoxFocused: {
    borderColor: colors.primary,
    borderWidth: 1.5,
  },
  input: {
    flex: 1,
    fontSize: 14,
    color: colors.onSurface,
    padding: 0,
    ...(Platform.OS === 'web' ? ({ outlineStyle: 'none' } as any) : {}),
  },
  inputSuffix: {
    fontSize: 13,
    color: colors.onSurfaceVariant,
    marginLeft: spacing[1],
  },
  // Dropdown
  dropdown: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: colors.outline,
    borderRadius: borderRadius.DEFAULT,
    paddingHorizontal: spacing[2.5],
    height: 38,
  },
  dropdownOpen: {
    borderColor: colors.primary,
    borderWidth: 1.5,
  },
  dropdownText: {
    fontSize: 14,
    color: colors.onSurface,
  },
  dropdownMenu: {
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: colors.outline,
    borderRadius: borderRadius.DEFAULT,
    marginTop: 4,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 4,
    elevation: 3,
  },
  dropdownOption: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: spacing[3],
    paddingVertical: spacing[2.5],
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.outlineVariant,
  },
  dropdownOptionSelected: {
    backgroundColor: '#F0FDF4',
  },
  dropdownOptionText: {
    fontSize: 14,
    color: colors.onSurface,
  },
  dropdownOptionTextSelected: {
    color: colors.primary,
    fontWeight: fontWeights.semibold,
  },
  // Stepper
  stepperRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[2],
  },
  stepperBtn: {
    width: 38,
    height: 38,
    borderWidth: 1,
    borderColor: colors.outline,
    borderRadius: borderRadius.DEFAULT,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#ffffff',
  },
  stepperBtnText: {
    fontSize: 20,
    fontWeight: fontWeights.semibold,
    color: colors.onSurface,
  },
  stepperValueBox: {
    flex: 1,
    height: 38,
    borderWidth: 1,
    borderColor: colors.outline,
    borderRadius: borderRadius.DEFAULT,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#ffffff',
  },
  stepperValueText: {
    fontSize: 14,
    color: colors.onSurface,
    fontWeight: fontWeights.semibold,
  },
  // Checkbox
  checkboxRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[2],
    marginBottom: spacing[2],
  },
  checkbox: {
    width: 20,
    height: 20,
    borderWidth: 1.5,
    borderColor: colors.outline,
    borderRadius: 4,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#ffffff',
  },
  checkboxChecked: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  checkboxLabel: {
    fontSize: 13,
    color: colors.onSurface,
    flex: 1,
  },
});